import type { IBranch } from "../VendorBody/VendorItems/VendorConnectionModal/types/IHeritageConnection";

/**
 * Heritage returns branches grouped under the customer's ship-to locations, and
 * the same branch can serve more than one of them. A flat list therefore shows
 * the same branch twice with nothing to tell the two apart, and hides the fact
 * that picking a branch also picks the location it ships from.
 *
 * These helpers turn the endpoint's flat rows back into that structure so the
 * picker can present it the way Heritage models it.
 */

export interface ShipToGroup {
  /** Stable key for rendering; the ship-to sequence, or "" when a row has none. */
  key: string;
  /** What the customer calls this location, falling back to its town, then its sequence. */
  label: string;
  /** The sequence Heritage identifies the location by, shown alongside the label. */
  sequenceNumber: string | null;
  branches: IBranch[];
}

/** Rows cached before ship-to was stored have no sequence; they group together. */
const UNGROUPED_KEY = "";

export function shipToKey(branch: IBranch): string {
  const seq = branch.ship_to_sequence_number;
  return seq == null ? UNGROUPED_KEY : String(seq).trim();
}

/**
 * A label a person recognises.
 *
 * The name is what the customer set up with Heritage, so it is preferred. Rows
 * cached before the name was stored fall back to the town, and then to the bare
 * sequence — never to an empty heading.
 */
export function shipToLabel(branch: IBranch): string {
  const name = branch.ship_to_name?.trim();
  if (name) return name;

  const city = branch.city?.trim();
  const state = branch.state?.trim();
  if (city && state) return `${city}, ${state}`;
  if (city) return city;

  const seq = branch.ship_to_sequence_number?.toString().trim();
  if (seq) return `Ship-to ${seq}`;

  return "Other branches";
}

/** Ship-to sequences are text but read as numbers, so 10 must not sort before 2. */
function compareSequences(a: string, b: string): number {
  const na = Number(a.replace(/\D/g, ""));
  const nb = Number(b.replace(/\D/g, ""));
  const aNumeric = a !== "" && Number.isFinite(na);
  const bNumeric = b !== "" && Number.isFinite(nb);

  if (aNumeric && bNumeric && na !== nb) return na - nb;
  if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
  return a.localeCompare(b);
}

/**
 * Group branches by the ship-to location they are reached from, preserving the
 * order the branches arrive in within each group.
 */
export function groupBranchesByShipTo(branches: IBranch[]): ShipToGroup[] {
  const groups = new Map<string, ShipToGroup>();

  for (const branch of branches) {
    const key = shipToKey(branch);
    const existing = groups.get(key);
    if (existing) {
      existing.branches.push(branch);
      continue;
    }
    groups.set(key, {
      key,
      label: shipToLabel(branch),
      sequenceNumber: key === UNGROUPED_KEY ? null : key,
      branches: [branch],
    });
  }

  // Rows with no ship-to last: they are leftovers from before it was cached, not
  // a location the customer would look for.
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === UNGROUPED_KEY) return 1;
    if (b.key === UNGROUPED_KEY) return -1;
    return compareSequences(a.key, b.key);
  });
}

/**
 * The branch the picker shows as chosen.
 *
 * A vendor's own setting wins, then an unsaved choice in the create-vendor flow,
 * then this user's default. The company-wide flag is last: it predates per-user
 * defaults and only applies to a user who has not chosen yet.
 */
export function resolveSelectedBranchId(
  branches: IBranch[],
  overrides: { vendorBranchId?: number | null; pendingBranchId?: number | null } = {}
): number | undefined {
  if (overrides.vendorBranchId != null) return overrides.vendorBranchId;
  if (overrides.pendingBranchId != null) return overrides.pendingBranchId;

  const userDefault = branches.find((b) => b.is_user_default);
  if (userDefault) return userDefault.id;

  return branches.find((b) => b.is_default)?.id;
}
